import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { IconRefresh, IconDownload, IconPrinter, IconPencil } from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import SaveButton from './SaveButton';
import photoCollectorService from '../../services/photoCollectorService';
import questionPaperService from '../../services/questionPaperService';
import { BoothPosterPreview, downloadBoothPoster, printBoothPoster, readBoothPosterPrefs, saveBoothPosterPrefs } from './BoothPoster';
import ConfirmDialog from './ConfirmDialog';
import { toBnDigits } from '../../utils/bnFormat';

const SIZE_OPTIONS = [10, 15, 20];
const EXPIRE_OPTIONS = [24, 48, 72];

// Small on/off switch (single source for the header + poster-display toggles).
const Switch = ({ checked, disabled, onChange }) => (
    <span className={`relative inline-block w-9 h-5 flex-none ${disabled ? 'opacity-40' : ''}`}>
        <input type="checkbox" className="sr-only peer" checked={checked} disabled={disabled} onChange={(e) => onChange(e.currentTarget.checked)} />
        <span className="absolute inset-0 rounded-full bg-zinc-300 dark:bg-zinc-600 peer-checked:bg-primary transition-colors" />
        <span className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform peer-checked:translate-x-4" />
    </span>
);

// Bengali daypart for a 24h hour (replaces AM/PM when the app language is bn).
const bnDaypart = (h) => (h < 4 ? 'রাত' : h < 12 ? 'সকাল' : h < 15 ? 'দুপুর' : h < 18 ? 'বিকাল' : h < 20 ? 'সন্ধ্যা' : 'রাত');

// "HH:mm" (business-profile TimePicker format) → display string.
// bn → "সকাল ৯:৩০" (daypart + Bengali digits); otherwise → "09:30 AM".
const displayTime = (v, lang) => {
    const m = String(v || '').match(/^(\d{1,2}):(\d{2})$/);
    if (!m) return v || '—';
    const h24 = parseInt(m[1], 10);
    const h = h24 % 12 === 0 ? 12 : h24 % 12;
    if (lang === 'bn') return `${bnDaypart(h24)} ${toBnDigits(h)}:${toBnDigits(m[2])}`;
    const period = h24 >= 12 ? 'PM' : 'AM';
    return `${String(h).padStart(2, '0')}:${m[2]} ${period}`;
};

// Placeholder shown while the collector config is still loading, so the card holds
// a stable height instead of popping in when the real poster + settings mount.
const BoothSkeleton = () => (
    <div className="mt-4 grid grid-cols-1 lg:grid-cols-[224px_1fr] gap-5 lg:gap-6 items-stretch animate-pulse">
        <div className="mx-auto lg:mx-0 w-full max-w-[224px] flex flex-col gap-2">
            <div className="w-full aspect-[5/7] rounded-xl bg-zinc-100 dark:bg-zinc-800" />
            <div className="flex gap-2">
                <div className="flex-1 h-9 rounded-lg bg-zinc-100 dark:bg-zinc-800" />
                <div className="w-20 h-9 rounded-lg bg-zinc-100 dark:bg-zinc-800" />
            </div>
        </div>
        <div className="flex flex-col gap-3.5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="h-[74px] rounded-xl bg-zinc-100 dark:bg-zinc-800" />
                <div className="h-[74px] rounded-xl bg-zinc-100 dark:bg-zinc-800" />
            </div>
            <div className="h-[58px] rounded-xl bg-zinc-100 dark:bg-zinc-800" />
            <div className="h-[68px] rounded-xl bg-zinc-100 dark:bg-zinc-800" />
            <div className="h-11 rounded-xl bg-zinc-100 dark:bg-zinc-800" />
            <div className="h-[68px] rounded-xl bg-zinc-100 dark:bg-zinc-800" />
        </div>
    </div>
);

/**
 * "Photo Booth Setting" card (Settings page). Live poster preview on the left,
 * staged settings on the right, coloured actions + Save along the bottom. The R2
 * shop logo is proxied to a data URL so the downloaded/printed poster canvas stays
 * untainted. Scoped to one office space.
 */
export default function PhotoCollectorSettings({ officeSpaceId, shopName, shopLogo, shopPhone, shopAddress, businessOpenTime, businessCloseTime }) {
    const { t, i18n } = useTranslation();
    const [config, setConfig] = useState(null);
    const [draft, setDraft] = useState({ enabled: false, maxQueueSize: 15, expireHours: 24, followBusinessHours: false, acceptFiles: false });
    const [saving, setSaving] = useState(false);
    const [logoDataUrl, setLogoDataUrl] = useState(null);
    const [showRegen, setShowRegen] = useState(false);
    // Poster render choices (show logo / address / phone) — local per office space.
    const [posterPrefs, setPosterPrefs] = useState(() => readBoothPosterPrefs(officeSpaceId));
    useEffect(() => { setPosterPrefs(readBoothPosterPrefs(officeSpaceId)); }, [officeSpaceId]);
    const setPosterPref = (patch) => setPosterPrefs(prev => {
        const next = { ...prev, ...patch };
        saveBoothPosterPrefs(officeSpaceId, next);
        return next;
    });

    useEffect(() => {
        let alive = true;
        if (!officeSpaceId) { setConfig(null); return; }
        (async () => {
            try {
                const res = await photoCollectorService.get(officeSpaceId);
                const col = res?.data?.collector || null;
                if (!alive) return;
                setConfig(col);
                if (col) setDraft({ enabled: col.enabled !== false, maxQueueSize: col.maxQueueSize || 15, expireHours: col.expireHours || 24, followBusinessHours: col.followBusinessHours === true, acceptFiles: col.acceptFiles === true });
            } catch { /* ignore */ }
        })();
        return () => { alive = false; };
    }, [officeSpaceId]);

    // Proxy the R2 shop logo → data URL (untainted canvas for download/print).
    useEffect(() => {
        let alive = true;
        if (!shopLogo) { setLogoDataUrl(null); return; }
        questionPaperService.proxyImageDataUrl(shopLogo).then(d => { if (alive) setLogoDataUrl(d); }).catch(() => {});
        return () => { alive = false; };
    }, [shopLogo]);

    const dirty = !!config && (
        draft.enabled !== (config.enabled !== false) ||
        draft.maxQueueSize !== (config.maxQueueSize || 15) ||
        draft.expireHours !== (config.expireHours || 24) ||
        draft.followBusinessHours !== (config.followBusinessHours === true) ||
        draft.acceptFiles !== (config.acceptFiles === true)
    );

    const handleSave = async () => {
        if (!officeSpaceId || !dirty || saving) return;
        setSaving(true);
        try {
            const res = await photoCollectorService.updateConfig(officeSpaceId, {
                enabled: draft.enabled, maxQueueSize: draft.maxQueueSize, expireHours: draft.expireHours,
                followBusinessHours: draft.followBusinessHours, acceptFiles: draft.acceptFiles,
            });
            setConfig(c => ({ ...c, ...(res?.data?.collector || {}) }));
            notifications.show({ title: t('photoCollectorPanel.savedTitle'), message: t('photoCollectorPanel.savedMsg'), color: 'green' });
        } catch (e) {
            notifications.show({ title: t('photoCollectorPanel.errorTitle'), message: e?.message || 'Failed', color: 'red' });
        } finally { setSaving(false); }
    };

    const regenerate = async () => {
        if (!officeSpaceId) return;
        try {
            const res = await photoCollectorService.regenerate(officeSpaceId);
            setConfig(c => ({ ...c, ...(res?.data?.collector || {}) }));
            notifications.show({ title: t('photoCollectorPanel.regeneratedTitle'), message: t('photoCollectorPanel.regeneratedMsg'), color: 'green' });
        } catch (e) {
            notifications.show({ title: t('photoCollectorPanel.errorTitle'), message: e?.message || 'Failed', color: 'red' });
        }
    };

    const posterOpts = () => ({
        uploadUrl: config?.uploadUrl, shopName, t,
        shopPhone: posterPrefs.showPhone ? shopPhone : '',
        shopLogoDataUrl: posterPrefs.showLogo ? logoDataUrl : null,
        shopAddress: posterPrefs.showAddress ? shopAddress : '',
        acceptFiles: draft.acceptFiles,
    });
    const doDownload = () => { if (config?.uploadUrl) downloadBoothPoster(posterOpts(), config.slug).catch(() => {}); };
    const doPrint = () => { if (config?.uploadUrl) printBoothPoster(posterOpts()).catch(() => {}); };

    if (!officeSpaceId) return null;
    const setD = (patch) => setDraft(d => ({ ...d, ...patch }));

    return (
        <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-xl shadow-sm px-4 sm:px-[22px] py-[18px]">
            {/* Header + labelled on/off (staged) */}
            <div className="flex flex-wrap items-center gap-3 sm:gap-[18px]">
                <div className="min-w-0 flex-1">
                    <p className="text-lg font-bold text-zinc-800 dark:text-zinc-100">{t('photoCollectorPanel.settingsTitle')}</p>
                    <p className="mt-1 text-xs leading-relaxed text-zinc-500 dark:text-zinc-400">{t(draft.acceptFiles ? 'photoCollectorPanel.settingsDescFiles' : 'photoCollectorPanel.settingsDesc')}</p>
                </div>
                <label className="flex-none flex items-center gap-2.5 cursor-pointer select-none rounded-xl border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/60 px-3.5 py-2.5">
                    <span className="text-xs font-bold text-zinc-700 dark:text-zinc-200">{t('photoCollectorPanel.enableLabel')}</span>
                    <Switch checked={draft.enabled} onChange={(v) => setD({ enabled: v })} />
                </label>
            </div>

            {/* While the config loads → skeleton (stable height, no pop-in).
                Booth OFF → hide the poster + every setting, leaving just this card's
                header and the Save row so the disabled state can still be persisted.
                Booth ON → the full body. */}
            {config === null ? (
                <BoothSkeleton />
            ) : !draft.enabled ? (
                <p className="mt-4 rounded-xl border border-dashed border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/40 px-4 py-6 text-center text-sm text-zinc-500 dark:text-zinc-400">
                    {t('photoCollectorPanel.disabledHint')}
                </p>
            ) : (
            /* Body: poster (left) + settings & actions (right). The columns stretch
                to equal height and the actions pin to the bottom so the right side
                fills instead of leaving a blank gap. */
            <div className="mt-4 grid grid-cols-1 lg:grid-cols-[224px_1fr] gap-5 lg:gap-6 items-stretch">
                {/* Left: live poster preview + download/print right below it */}
                <div className="mx-auto lg:mx-0 w-full max-w-[224px] flex flex-col gap-2">
                    <BoothPosterPreview className="w-full" uploadUrl={config?.uploadUrl} shopName={shopName}
                        shopLogoDataUrl={posterPrefs.showLogo ? logoDataUrl : null}
                        shopPhone={posterPrefs.showPhone ? shopPhone : ''}
                        shopAddress={posterPrefs.showAddress ? shopAddress : ''}
                        acceptFiles={draft.acceptFiles} />
                    <div className="flex gap-2">
                        <button onClick={doDownload} className="flex-1 inline-flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-[13px] font-semibold text-white bg-green-600 hover:bg-green-700 transition-colors">
                            <IconDownload size={15} /> {t('photoCollectorPanel.poster')}
                        </button>
                        <button onClick={doPrint} className="flex-none inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[13px] font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors">
                            <IconPrinter size={15} /> {t('photoCollectorPanel.print')}
                        </button>
                    </div>
                </div>

                {/* Right: settings (top) + actions (pinned bottom) */}
                <div className="flex flex-col gap-3.5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="rounded-xl border border-zinc-200 dark:border-zinc-700 p-3.5">
                            <p className="text-sm !font-bold mb-2 text-zinc-700 dark:text-zinc-200">{t(draft.acceptFiles ? 'photoCollectorPanel.maxSizeLabelFiles' : 'photoCollectorPanel.maxSizeLabel')}</p>
                            <div className="flex gap-1.5">
                                {SIZE_OPTIONS.map(n => (
                                    <button key={n} onClick={() => setD({ maxQueueSize: n })}
                                        className={`flex-1 px-2 py-2 rounded-lg text-sm font-semibold border transition-colors ${draft.maxQueueSize === n ? 'border-primary bg-primary/10 text-primary' : 'border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800'}`}>
                                        {t('photoCollectorPanel.sizeOption', { n: i18n.language === 'bn' ? toBnDigits(n) : n })}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="rounded-xl border border-zinc-200 dark:border-zinc-700 p-3.5">
                            <p className="text-sm !font-bold mb-2 text-zinc-700 dark:text-zinc-200">{t(draft.acceptFiles ? 'photoCollectorPanel.expireLabelFiles' : 'photoCollectorPanel.expireLabel')}</p>
                            <div className="flex gap-1.5">
                                {EXPIRE_OPTIONS.map(h => (
                                    <button key={h} onClick={() => setD({ expireHours: h })}
                                        className={`flex-1 px-2 py-2 rounded-lg text-sm font-semibold border transition-colors ${draft.expireHours === h ? 'border-primary bg-primary/10 text-primary' : 'border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800'}`}>
                                        {h === 24 ? t('photoCollectorPanel.h24') : h === 48 ? t('photoCollectorPanel.h48') : t('photoCollectorPanel.h72')}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* The PDF switch and the poster switches share one row on wide screens (stacked on phones). */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {/* Receive PDF files too. OFF by default = photos only; the customer's upload
                        page and the poster title ("Send photo / file") follow this switch. */}
                    <label className="flex items-center gap-3 rounded-xl border border-zinc-200 dark:border-zinc-700 px-3.5 py-3 cursor-pointer select-none">
                        <span className="min-w-0 flex-1">
                            <span className="block text-sm font-bold text-zinc-700 dark:text-zinc-200">{t('photoCollectorPanel.acceptFilesLabel')}</span>
                            <span className="block text-[11px] leading-relaxed text-zinc-500 dark:text-zinc-400 mt-0.5">
                                {t(draft.acceptFiles ? 'photoCollectorPanel.acceptFilesOnHint' : 'photoCollectorPanel.acceptFilesOffHint')}
                            </span>
                        </span>
                        <Switch checked={draft.acceptFiles} onChange={(v) => setD({ acceptFiles: v })} />
                    </label>

                    {/* Which optional bits the poster shows — logo & address are OFF by
                        default, phone ON. Local render prefs (apply instantly, also used
                        by the generate-image booth panel). A toggle is disabled when the
                        business profile doesn't have that piece of info yet. */}
                    <div className="rounded-xl border border-zinc-200 dark:border-zinc-700 px-3.5 py-3">
                        <p className="text-sm !font-bold mb-2.5 text-zinc-700 dark:text-zinc-200">{t('photoCollectorPanel.posterShowTitle')}</p>
                        {/* Each switch sits tight beside its own label — spreading the
                            pairs apart makes a switch look like it belongs to the NEXT label. */}
                        <div className="flex flex-wrap items-center gap-x-7 gap-y-2.5">
                            {[
                                { key: 'showLogo', label: t('photoCollectorPanel.showLogo'), disabled: !shopLogo },
                                { key: 'showAddress', label: t('photoCollectorPanel.showAddress'), disabled: !(shopAddress || '').trim() },
                                { key: 'showPhone', label: t('photoCollectorPanel.showPhone'), disabled: !(shopPhone || '').trim() },
                            ].map(({ key, label, disabled }) => (
                                <label key={key} className={`flex items-center gap-2 select-none ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
                                    <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">{label}</span>
                                    <Switch checked={posterPrefs[key] === true && !disabled} disabled={disabled} onChange={(v) => setPosterPref({ [key]: v })} />
                                </label>
                            ))}
                        </div>
                    </div>
                    </div>

                    {/* Booth follows the business profile's open/close hours (optional).
                        Unchecked → booth stays open around the clock (while enabled). */}
                    <div className="rounded-xl border border-zinc-200 dark:border-zinc-700 px-3.5 py-3">
                        <div className="flex items-start gap-2.5">
                            <label className={`flex-1 min-w-0 flex items-start gap-2.5 select-none ${businessOpenTime && businessCloseTime ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}>
                                <input type="checkbox" className="mt-0.5 w-4 h-4 accent-primary flex-none"
                                    checked={draft.followBusinessHours === true}
                                    disabled={!(businessOpenTime && businessCloseTime)}
                                    onChange={(e) => setD({ followBusinessHours: e.currentTarget.checked })} />
                                <span className="min-w-0">
                                    <span className="block text-xs font-semibold text-zinc-700 dark:text-zinc-200">{t('photoCollectorPanel.bizHoursLabel')}</span>
                                    {businessOpenTime && businessCloseTime ? (
                                        <span className="block text-[11px] text-zinc-500 dark:text-zinc-400 mt-0.5">
                                            {t('photoCollectorPanel.bizHoursTime', { open: displayTime(businessOpenTime, i18n.language), close: displayTime(businessCloseTime, i18n.language) })}
                                            {' — '}{draft.followBusinessHours ? t('photoCollectorPanel.bizHoursOnHint') : t('photoCollectorPanel.bizHoursOffHint')}
                                        </span>
                                    ) : (
                                        <span className="block text-[11px] text-zinc-400 dark:text-zinc-500 mt-0.5">{t('photoCollectorPanel.bizHoursNone')}</span>
                                    )}
                                </span>
                            </label>
                            {/* Open/close times live on the office's business profile */}
                            <Link to={`/officespaces/${officeSpaceId}/settings`}
                                className="flex-none inline-flex items-center gap-1 text-[11px] font-semibold text-primary hover:underline">
                                <IconPencil size={12} /> {t('photoCollectorPanel.bizHoursEdit')}
                            </Link>
                        </div>
                    </div>

                    {/* Permanent-QR note + de-emphasised regenerate */}
                    <p className="text-[11px] text-zinc-400 dark:text-zinc-500 leading-relaxed">
                        {t('photoCollectorPanel.permanentQrNote')}{' '}
                        <button onClick={() => setShowRegen(true)} className="text-primary hover:underline font-medium whitespace-nowrap inline-flex items-center gap-0.5 align-baseline">
                            <IconRefresh size={11} /> {t('photoCollectorPanel.regenerate')}
                        </button>
                    </p>

                </div>
            </div>
            )}

            {/* Save — always visible (even when the booth is off) so enabling or
                disabling the booth can be persisted. */}
            <div className="mt-4 flex items-center justify-end pt-3 border-t border-zinc-100 dark:border-zinc-800">
                <SaveButton dirty={dirty} saving={saving} onClick={handleSave} />
            </div>

            {/* Regenerate confirmation — the QR is permanent, so warn first */}
            <ConfirmDialog
                open={showRegen}
                onClose={() => setShowRegen(false)}
                onConfirm={() => { setShowRegen(false); regenerate(); }}
                title={t('photoCollectorPanel.regenConfirmTitle')}
                description={t('photoCollectorPanel.regenConfirmMsg')}
                confirmLabel={t('photoCollectorPanel.regenConfirmYes')}
                cancelLabel={t('photoCollectorPanel.cancel')}
                variant="primary"
            />
        </div>
    );
}
